<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { QrcodeStream } from 'vue-qrcode-reader'

const props = defineProps<{
  show: boolean
  onQRDetect: (detectedCodes: any[]) => void
  onCameraReady: (capabilities: any) => void
  onScannerError: (error: any) => void
  selectedCameraId?: string
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  'join': [code: string]
}>()

const joinCode = defineModel<string>('joinCode', { default: '' })
const localScannerActive = ref(false)

// Constraints computadas - usando el formato correcto para vue-qrcode-reader
const cameraConstraints = computed(() => {
  if (props.selectedCameraId) {
    console.log('[JoinModal] 🎯 Usando deviceId específico:', props.selectedCameraId.slice(0, 8) + '...')
    return {
      deviceId: { exact: props.selectedCameraId },
      width: { ideal: 1280 },
      height: { ideal: 720 }
    }
  }
  console.log('[JoinModal] 📷 Sin deviceId, usando facingMode')
  return { facingMode: 'environment' }
})

const handleDetect = (detectedCodes: any[]) => {
  console.log('[JoinModal] ✅ QR detectado:', detectedCodes)
  props.onQRDetect(detectedCodes)
}

const handleCameraOn = (capabilities: any) => {
  console.log('[JoinModal] 📷 Cámara lista con capabilities:', capabilities)
  props.onCameraReady(capabilities)
}

const handleError = (error: any) => {
  console.error('[JoinModal] ❌ Error:', error)
  props.onScannerError(error)
}

// Watcher para detectar cambios en selectedCameraId
watch(() => props.selectedCameraId, (newId) => {
  if (newId) {
    console.log('[JoinModal] 📷 CameraId prop cambiado:', newId.slice(0, 8) + '...')
  }
})

// Debug al montar
onMounted(() => {
  console.log('[JoinModal] 🚀 Componente montado')
  console.log('[JoinModal] 📷 Cámara seleccionada:', props.selectedCameraId?.slice(0, 8) + '...' || 'NINGUNA')
  console.log('[JoinModal] 🎯 Constraints actuales:', JSON.stringify(cameraConstraints.value, null, 2))
})

const close = () => {
  emit('update:show', false)
  joinCode.value = ''
  localScannerActive.value = false
}

const handleJoin = () => {
  emit('join', joinCode.value)
  close()
}

const toggleScanner = () => {
  localScannerActive.value = !localScannerActive.value
}
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50">
    <div class="glass rounded-3xl p-6 max-w-md w-full animate-fade-in">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-white">🔗 Unirse a Sala</h2>
        <button @click="close" class="text-white text-2xl hover:scale-110 transition-transform">✕</button>
      </div>

      <div class="mb-6">
        <p class="text-teal-200 text-sm mb-3">Escanea el código QR del host:</p>

        <!-- QR Scanner with vue-qrcode-reader -->
        <div v-if="localScannerActive && selectedCameraId" class="bg-black rounded-xl overflow-hidden mb-3" style="min-height: 250px; width: 100%;">
          <QrcodeStream
            :key="selectedCameraId"
            :constraints="cameraConstraints"
            @detect="handleDetect"
            @camera-on="handleCameraOn"
            @error="handleError"
          />
        </div>

        <button
          @click="toggleScanner"
          :disabled="!selectedCameraId"
          class="w-full py-3 px-6 bg-accent text-dark font-bold rounded-xl hover:scale-105 transition-transform disabled:opacity-50"
        >
          📷 {{ localScannerActive ? 'Detener Cámara' : 'Iniciar Cámara' }}
        </button>
      </div>

      <div class="flex items-center gap-4 mb-6">
        <div class="flex-1 h-px bg-white/20"></div>
        <span class="text-teal-200 text-sm">o</span>
        <div class="flex-1 h-px bg-white/20"></div>
      </div>

      <div class="mb-6">
        <p class="text-teal-200 text-sm mb-3">Pega el código de la sala:</p>
        <textarea
          v-model="joinCode"
          placeholder="Pega aquí el código..."
          class="w-full bg-white/10 text-white rounded-xl p-3 border border-white/20 focus:border-accent focus:outline-none resize-none"
          rows="3"
        ></textarea>
      </div>

      <button
        @click="handleJoin"
        :disabled="!joinCode.trim()"
        class="w-full py-4 px-6 bg-accent text-dark font-bold rounded-xl text-lg hover:scale-105 transition-transform disabled:opacity-50"
      >
        🚀 Unirse
      </button>
    </div>
  </div>
</template>
